import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector } from './reduxHooks';
import { setSelectedFactory } from '../store/slices/factorySlice';
import { isGeneralAdmin } from '../utils/access';

/**
 * Fabrika kapsamının tek kaynağı. Üst menüdeki seçici ve sayfalardaki fabrika filtresi
 * aynı değeri okur ve değiştirir; liste istekleri `factoryParams`'ı açıkça ekler.
 *
 * Sadece genel admin fabrika seçebilir. Fabrika yöneticisi ve sürücü için backend zaten
 * kullanıcının kendi fabrikasını uygular, bu yüzden onlarda parametre gönderilmez.
 */
export const useFactoryScope = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const selectedFactoryId = useAppSelector((state) => state.factory.selectedFactoryId);
  const canSelectFactory = isGeneralAdmin(user);
  const factoryId = canSelectFactory ? selectedFactoryId : null;

  const setFactoryId = useCallback(
    (id: number | null) => {
      dispatch(setSelectedFactory(id));
    },
    [dispatch]
  );

  // Değer değişmedikçe aynı nesne döner; useCallback/useEffect bağımlılıklarında güvenle kullanılır.
  const factoryParams = useMemo(() => (factoryId ? { factory: factoryId } : {}), [factoryId]);

  return { factoryId, setFactoryId, factoryParams, canSelectFactory };
};

/**
 * Fabrika seçimi değiştiğinde (ilk açılışta değil) çalışır. Butonla arama yapılan listeler
 * bununla eski fabrikanın sürücü/araç seçimini temizler ve aramayı yeni fabrikayla tekrarlar.
 */
export const useOnFactoryChange = (onChange: () => void) => {
  const { factoryId } = useFactoryScope();
  const previousFactoryId = useRef(factoryId);
  // Her çizimde en güncel fonksiyon (yeni fabrikanın parametreleriyle) saklanır
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (previousFactoryId.current === factoryId) {
      return;
    }
    previousFactoryId.current = factoryId;
    onChangeRef.current();
  }, [factoryId]);
};
